// src/admin/pages/ChangePassword.tsx
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useAdmin, { BASE_URL } from '../hooks/useAdmin';
import PasswordStrength from '../../shared/components/PasswordStrength';
import ErrorDisplay from '../../shared/components/ErrorDisplay';

interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export default function ChangePassword() {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<ChangePasswordForm>();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { error, setError, refreshCsrf } = useAdmin();
  const navigate = useNavigate();

  const onSubmit = async (data: ChangePasswordForm) => {
    if (data.newPassword !== data.confirmPassword) {
      setError('New password and confirm password do not match');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const csrfToken = await refreshCsrf();
      if (!csrfToken) throw new Error('No CSRF token');

      const res = await fetch(`${BASE_URL}/admin/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        }),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Failed to change password');

      alert('Password changed successfully');
      navigate('/admin/dashboard');
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">Change Password</h2>

        {error && <ErrorDisplay error={error} onDismiss={() => setError('')} />}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <input type="hidden" name="_csrf" />

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="currentPassword">
              Current Password
            </label>
            <input
              id="currentPassword"
              type={showCurrentPassword ? 'text' : 'password'}
              placeholder="Current Password"
              {...register('currentPassword', { required: 'Current password required' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-12 focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              aria-invalid={!!errors.currentPassword}
            />
            <button
              type="button"
              aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-9 text-gray-600 transform -translate-y-1/2"
            >
              {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
            {errors.currentPassword && <p className="text-red-600 text-sm">{errors.currentPassword.message}</p>}
          </div>

          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="newPassword">
              New Password
            </label>
            <input
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New Password"
              {...register('newPassword', {
                required: 'New password required',
                minLength: { value: 8, message: 'Password must be at least 8 characters' },
              })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg pr-12 focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              aria-invalid={!!errors.newPassword}
            />
            <button
              type="button"
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-9 text-gray-600 transform -translate-y-1/2"
            >
              {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>

            <PasswordStrength password={watch('newPassword') || ''} />

            {errors.newPassword && <p className="text-red-600 text-sm">{errors.newPassword.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="confirmPassword">
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="Confirm New Password"
              {...register('confirmPassword', { required: 'Confirm password required' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
              aria-invalid={!!errors.confirmPassword}
            />
            {errors.confirmPassword && <p className="text-red-600 text-sm">{errors.confirmPassword.message}</p>}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition transform hover:scale-105 disabled:opacity-70"
          >
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}